/**
 * Admin impersonation helpers — single source of truth for reading,
 * writing, and clearing the "viewing as merchant" state in localStorage.
 *
 * The data shape is tiny but lives in two keys for separation of
 * concerns:
 *   • `neptune_merchant_token`   — the actual session bearer (also
 *     used by real merchant sessions; reusing it means every merchant
 *     route just works during impersonation with zero code changes).
 *   • `neptune_impersonation`    — the metadata flag that tells the
 *     banner "we are in impersonation mode" + which merchant + how
 *     long left + where to return to when the AM hits Exit.
 *
 * If a real merchant ever signs in on the same browser they'll only
 * clobber the token, never the impersonation flag — so the banner
 * stops on its own once the underlying token no longer matches.
 */
import { merchantTokenStore } from "./merchantApi";

const IMPERSONATION_KEY = "neptune_impersonation";


export const impersonationStore = {
  get() {
    try {
      const raw = localStorage.getItem(IMPERSONATION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Auto-evict if the server-set TTL already elapsed. We still
      // keep the *merchant token* — the merchant API will 401 on its
      // own and surface the right "session expired" UX — but the
      // banner stops claiming we're still impersonating.
      if (parsed?.expires_at) {
        const exp = new Date(parsed.expires_at).getTime();
        if (!isNaN(exp) && exp < Date.now()) {
          localStorage.removeItem(IMPERSONATION_KEY);
          return null;
        }
      }
      return parsed;
    } catch {
      return null;
    }
  },
  set(meta) {
    try { localStorage.setItem(IMPERSONATION_KEY, JSON.stringify(meta)); } catch { /* ignore */ }
  },
  clear() {
    try { localStorage.removeItem(IMPERSONATION_KEY); } catch { /* ignore */ }
  },
  isActive() {
    return !!impersonationStore.get();
  },
};


/**
 * Begin an impersonation session locally — the caller must already
 * have a fresh token from `POST /admin/merchants/{mid}/impersonate`.
 * We write both the bearer token and the metadata atomically and
 * return so the caller can navigate.
 */
export function applyImpersonation({
  token, merchant_id, merchant_legal_name, merchant_email,
  merchant_ref, expires_at, return_to,
}) {
  merchantTokenStore.set(token);
  impersonationStore.set({
    merchant_id,
    merchant_legal_name: merchant_legal_name || merchant_email || merchant_id,
    merchant_email,
    merchant_ref,
    expires_at,
    started_at: new Date().toISOString(),
    return_to: return_to || `/admin/merchants/${merchant_id}`,
  });
}


/**
 * End impersonation — clears local state. The caller is responsible
 * for the server-side `POST /impersonate/exit` revoke (best-effort).
 */
export function clearImpersonation() {
  merchantTokenStore.clear();
  impersonationStore.clear();
}
